import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryData, usePostMessage } from 'spectacle-ui-game-bridge-react';
import { usePreloginContext } from '../../context/PreloginContext';
import { getPath } from '../../config';
import Debug from '../../components/debug/debug';
import ErrorModal, { ErrorMessage } from '../../components/errorModal/errorModal';
import useBodyClass from '../../hooks/useBodyClass';
import useContentForBuild from '../../hooks/useContentForBuild';
import useDlogLoginFunnel from '../../hooks/useDlogLoginFunnel';

export default function Loading() {
  const { isCDNIFinished, isMobileSharedAccountEnabled, build, hasValidToken, isUsingCachedLoginToken, systemCountry } = usePreloginContext();
  const isKoreanRegion = systemCountry === 'KR';
  const isUKRegion = systemCountry === 'UK';
  const [err, setErr] = useState<ErrorMessage | null>(null);
  const [debugState, setDebugState] = useState<string>('');
  const navigate = useNavigate();
  const dlog = useDlogLoginFunnel();
  const [acceptedCodeOfConductVersion, acceptedCodeOfConductVersionError] = useQueryData('game', 'codeofconductversion');
  const [isReturningFromBan, isReturningFromBanError] = useQueryData('game', 'isReturningFromBan');
  const [acceptedPrivacyPolicyVersion, acceptedPrivacyPolicyVersionError] = useQueryData('game', 'privacypolicyversion');
  const [acceptedTosVersion, acceptedTosVersionError] = useQueryData('game', 'tosversion');
  const [acceptedTouVersion, acceptedTouVersionError] = useQueryData('game', 'touversion');
  const [acceptedPIIVersion, acceptedPIIVersionError] = useQueryData('game', 'piiversion'); // KO Only
  const closeWebView = usePostMessage('closeWebView');
  const passPreloginFence = usePostMessage('passPreloginFence');
  const sysReady = usePostMessage('sysReady');
  const scheduleReminderNotification = usePostMessage('scheduleReminderNotification');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [content, loading, error] = useContentForBuild();

  useBodyClass('splash');

  const retry = () => {
    setErr(null);
    window.location.reload();
  };

  useEffect(() => {
    dlog({
      phase: 'Studio_Splash',
      action: 'Render',
    });
  }, [dlog]);

  useEffect(() => {
    if (content) {
      if (content.enableReminderNotification) {
        scheduleReminderNotification(
          JSON.stringify({
            notificationName: content.reminderNotificationName,
            delay: Number(content.reminderNotificationDelay),
            notificationTitle: content.reminderNotificationTitle,
            notificationBody: content.reminderNotificationBody,
          })
        );
      }

      // If the server is down, redirect to the status page
      if (Boolean(content.Status)) {
        navigate('/status');
        return;
      }

      // Ensure we have values for all acceptance versions
      if ((!acceptedTosVersion && acceptedTosVersion !== '') || (!acceptedPrivacyPolicyVersion && acceptedPrivacyPolicyVersion !== '') || (!acceptedCodeOfConductVersion && acceptedCodeOfConductVersion !== '' && !isKoreanRegion) || (!acceptedTouVersion && acceptedTouVersion !== '') || (isKoreanRegion && !acceptedPIIVersion && acceptedPIIVersion !== '')) {
        console.error('Missing acceptance versions');
        setDebugState('Missing acceptance versions');
        return;
      }

      // Check all acceptance versions, if they match the aggrement versions, set the server status to ok
      if ((acceptedTosVersion === content.serviceAgreementVersion && acceptedPrivacyPolicyVersion === content.privacyPolicyAgreementVersion && acceptedCodeOfConductVersion === content.codeOfConductAgreementVersion && !isKoreanRegion && acceptedTouVersion === content.termsOfUseAgreementVersion) || (isKoreanRegion && acceptedPIIVersion === content.koTermsPIICollectionVersion)) {
        // If the player is returning from a temporary ban, send to re-accept the code of conduct
        if (isReturningFromBan === 1) {
          setDebugState('Returning from a Ban');
          navigate('/code-of-conduct');
        }

        if (content.webBasedLogin) {
          // Drive to CDNI if it's not finished yet or if we are auto logging the user in
          if (typeof isCDNIFinished === 'boolean' && !isCDNIFinished) {
            navigate('/cdni');
          } else if (typeof isCDNIFinished === 'boolean') {
            // If not logged in, drive to login page
            passPreloginFence();

            // Drive to Upsell
            if (isMobileSharedAccountEnabled && !isUsingCachedLoginToken && isCDNIFinished) {
              navigate('/upsell');
            }

            if (!hasValidToken) {
              navigate('/login');
            } else if (hasValidToken) {
              setDebugState('Has valid login token');

              if (content.enablePostLoginMessages) {
                navigate('/post-login');
              } else {
                closeWebView();
              }
            }
          }
        } else {
          passPreloginFence();
        }
      } else {
        if ((window as any).hasPassedAcceptance === true && !hasValidToken) {
          navigate('/login');
        } else {
          if (isKoreanRegion && content.enableKOAcceptance) {
            navigate('/acceptance-ko');
          } else if (isUKRegion && content.enableUKAcceptance) {
            navigate('/age-gate');
          } else {
            navigate('/acceptance');
          }
        }
      }
    }
  }, [acceptedCodeOfConductVersion, acceptedPrivacyPolicyVersion, acceptedTosVersion, acceptedTouVersion, acceptedPIIVersion, isUsingCachedLoginToken, content, passPreloginFence, navigate, isReturningFromBan, closeWebView, isCDNIFinished, hasValidToken, isMobileSharedAccountEnabled, scheduleReminderNotification, isKoreanRegion, isUKRegion]);

  useEffect(() => {
    // If we have content, the system is ready
    if (content) {
      sysReady();
    }
  }, [sysReady, content]);

  useEffect(() => {
    // Ensure we have no game bridge or localization errors
    const _error = [acceptedCodeOfConductVersionError, acceptedPrivacyPolicyVersionError, acceptedTosVersionError, acceptedTouVersionError, acceptedPIIVersionError, isReturningFromBanError, error].filter(Boolean)[0];

    if (_error && content) {
      setErr({
        heading: content.error,
        message: content?.gameBridgeError ?? error,
        cta: content.ok,
        exit: retry,
      });
    }
  }, [acceptedCodeOfConductVersionError, acceptedPrivacyPolicyVersionError, acceptedTosVersionError, acceptedTouVersionError, acceptedPIIVersionError, isReturningFromBanError, error, content]);

  return (
    <>
      <div className="splash__container">
        <img src={getPath('splash')} alt="splash" />
      </div>
      <ErrorModal error={err} />
      <Debug
        state={debugState}
        enabled={!!content?.showDeveloperOverlay}
        vars={{
          isUsingCachedLoginToken,
          acceptedCodeOfConductVersion,
          isReturningFromBan,
          acceptedPrivacyPolicyVersion,
          acceptedTosVersion,
          acceptedTouVersion,
          hasValidToken,
          build,
          error,
        }}
      >
        <button
          onClick={() => {
            passPreloginFence();
            closeWebView();
          }}
        >
          Pass Prelogin Fence & Close Webview
        </button>
      </Debug>
    </>
  );
}
